import { Check, ChevronsUpDown, Loader2, SearchIcon } from "lucide-react";
import { Popover } from "radix-ui";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface AsyncComboboxProps<T> {
  /** Currently-selected value, or null when nothing is chosen. */
  value: string | null;
  /** Fired when an option is picked. Receives both the value and the full option. */
  onChange: (value: string, option: T) => void;

  /** Options for the current search term (the caller fetches these, typically via TanStack Query). */
  options: T[];
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => ReactNode;
  /** Plain-text label for the trigger when an option is selected. Defaults to `String(getOptionLabel)`. */
  getOptionText?: (option: T) => string;

  /** Controlled search term + handler — the caller debounces and fetches. */
  search: string;
  onSearchChange: (search: string) => void;

  /** Fallback trigger label when a value is set but its option isn't in the current results. */
  selectedLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;

  /**
   * Opt-in "use what I typed" affordance for fields whose value set isn't
   * fully known in advance (e.g. a reference id that hasn't been seen yet).
   * When both are set and the search term doesn't exactly match an existing
   * option, a synthetic `Use "<text>"` row is appended, built via
   * `getCustomOption` and selected exactly like a normal option. Omitting
   * either prop leaves behavior unchanged (pick-from-list only).
   */
  allowCustomValue?: boolean;
  getCustomOption?: (text: string) => T;
}

/**
 * A single-select combobox backed by server-side search. Presentational: it owns
 * the open/highlight UI state, the caller owns the data (search term + options).
 * Pair it with a TanStack Query hook — see `CustomerSelect` / `ProductSelect`.
 */
export function AsyncCombobox<T>({
  value,
  onChange,
  options,
  getOptionValue,
  getOptionLabel,
  getOptionText,
  search,
  onSearchChange,
  selectedLabel,
  loading = false,
  disabled = false,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results.",
  className,
  allowCustomValue = false,
  getCustomOption,
}: AsyncComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  // Remember the chosen option so the trigger keeps its label even after the
  // search term changes and that option drops out of `options`.
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  function optionText(option: T): string {
    if (getOptionText) return getOptionText(option);
    const label = getOptionLabel(option);
    return typeof label === "string" ? label : String(getOptionValue(option));
  }

  // Defensive: the caller contract is `options: T[]`, but a mismatched cache
  // entry (e.g. a query-key collision) could hand back a non-array shape —
  // guard here rather than trust every caller, so a shape bug degrades to an
  // empty list instead of crashing the page.
  const safeOptions = Array.isArray(options) ? options : [];

  const trimmedSearch = search.trim();
  const hasExactMatch = safeOptions.some(
    (option) => optionText(option).toLowerCase() === trimmedSearch.toLowerCase(),
  );
  const customOption =
    allowCustomValue && getCustomOption && trimmedSearch && !hasExactMatch
      ? getCustomOption(trimmedSearch)
      : null;
  const displayOptions = customOption ? [...safeOptions, customOption] : safeOptions;
  const customOptionIndex = customOption ? displayOptions.length - 1 : -1;

  // Keep the keyboard-highlighted option scrolled into view as you arrow past
  // the visible rows (the list is capped at max-h-60 and scrolls).
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${highlighted}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [open, highlighted]);

  const triggerLabel =
    selectedOption && getOptionValue(selectedOption) === value
      ? optionText(selectedOption)
      : (selectedLabel ?? null);

  function handleSearchChange(next: string) {
    onSearchChange(next);
    setHighlighted(0);
  }

  function select(option: T) {
    setSelectedOption(option);
    onChange(getOptionValue(option), option);
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((index) => Math.min(displayOptions.length - 1, index + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((index) => Math.max(0, index - 1));
    } else if (event.key === "Enter") {
      const option = displayOptions[highlighted];
      if (option) {
        event.preventDefault();
        select(option);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          // Closing via outside interaction (click-away, tab-out, re-toggling
          // the trigger). If the field is still empty and the user typed a
          // value without explicitly clicking/Entering the "Use "<text>""
          // row, commit it now — otherwise the typed text is silently
          // discarded, the field stays empty, and a required-field validator
          // blocks submit with no visible error. Only auto-commits when
          // `value` is empty, so reopening an already-set field to
          // browse/filter and clicking away without selecting still
          // preserves the existing value. Escape's own handler
          // (handleKeyDown) bypasses this entirely — Escape means cancel.
          if (!value && customOption) {
            select(customOption);
          } else {
            setOpen(false);
          }
          return;
        }
        setOpen(true);
        setHighlighted(0);
      }}
    >
      <Popover.Trigger
        type="button"
        disabled={disabled}
        className={cn(
          "border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        <span className={cn("line-clamp-1 text-left", !triggerLabel && "text-muted-foreground")}>
          {triggerLabel ?? placeholder}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-md border shadow-md"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="flex items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              className="h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-activedescendant={
                displayOptions.length > 0 ? `${listboxId}-option-${highlighted}` : undefined
              }
              aria-autocomplete="list"
            />
            {loading && <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />}
          </div>
          <ul ref={listRef} id={listboxId} className="max-h-60 overflow-y-auto p-1">
            {displayOptions.length === 0 ? (
              <li className="px-2 py-6 text-center text-sm text-muted-foreground">
                {loading ? "Searching…" : emptyText}
              </li>
            ) : (
              displayOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const isSelected = optionValue === value;
                const isHighlighted = index === highlighted;
                const isCustom = index === customOptionIndex;
                return (
                  <li key={optionValue}>
                    <button
                      type="button"
                      id={`${listboxId}-option-${index}`}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => select(option)}
                      onMouseEnter={() => setHighlighted(index)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none",
                        isHighlighted && "bg-accent text-accent-foreground",
                      )}
                    >
                      <span className="line-clamp-1">
                        {isCustom ? `Use "${trimmedSearch}"` : getOptionLabel(option)}
                      </span>
                      {isSelected && <Check className="size-4 shrink-0" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
